import React, { useCallback, useMemo, type ReactNode } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import type { StatusCategory } from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { asyncInlineEditStatusModal } from '@atlassian/jira-inline-edit-status-modal/entrypoint.tsx';
import { defaultModalProps } from '@atlassian/jira-issue-status-and-types-common/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { LIST_VIEW_SOURCE } from '@atlassian/jira-issue-status-and-types-common/src/types.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { editStatusExperience } from '../../../../experiences.tsx';
import { messages } from './messages.tsx';

type Props = {
	children: ReactNode;
	queryParams: {
		status: {
			id: string;
			statusName: string;
			statusCategory: StatusCategory;
		};
		projectId: string;
		projectKey: string;
		issueTypeId: string;
		statusNames: string[];
		onClick?: () => void;
		onClose?: () => void;
		onStatusUpdated?: () => Promise<void>;
	};
};

export const EditStatusModalEntrypointTrigger = ({ children, queryParams }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { onClose, projectKey, ...props } = queryParams;
	const { formatMessage } = useIntl();

	const onCloseEditStatusModal = useCallback(() => {
		onClose?.();
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'inlineEditStatusModal',
				action: 'closed',
			}),
		);
	}, [createAnalyticsEvent, onClose]);

	const entryPointProps = useMemo(
		() => ({
			...props,
			projectKey,
			editStatusExperience,
			source: LIST_VIEW_SOURCE,
			onClose: onCloseEditStatusModal,
		}),
		[props, projectKey, onCloseEditStatusModal],
	);

	return (
		<ModalEntryPointPressableTrigger
			entryPoint={asyncInlineEditStatusModal}
			entryPointProps={entryPointProps}
			interactionName="edit-status-modal"
			modalProps={defaultModalProps}
			useInternalModal
		>
			{({ ref }) => (
				<Box
					xcss={editButtonStyles}
					ref={ref}
					aria-label={formatMessage(messages.statusNameButtonLabel, {
						statusName: props.status?.statusName,
					})}
				>
					{children}
				</Box>
			)}
		</ModalEntryPointPressableTrigger>
	);
};

const editButtonStyles = xcss({
	marginBlock: 'space.negative.025',
});
